/* eslint-disable @typescript-eslint/no-explicit-any */
export type AskResponse = {
    answer: string;
    citations: Citation[];
    error?: string;
};

export type Citation = {
    content: string;
    id: string;
    title: string | null;
    filepath: string | null;
    url: string | null;
    metadata: string | null;
    chunk_id: string | null;
    reindex_id: string | null;
}

export type ToolMessageContent = {
    citations: Citation[];
    intent: string;
}

export type ChatMessage = {
    id: string;
    role: string;
    content: string;
    end_turn?: boolean;
    date: string;
};

export type Conversation = {
    id: string;
    title: string;
    messages: ChatMessage[];
    date: string;
}

export enum ChatCompletionType {
    ChatCompletion = "chat.completion",
    ChatCompletionChunk = "chat.completion.chunk"
}

export type ChatResponseChoice = {
    messages: ChatMessage[];
}

export type ChatResponse = {
    id: string;
    model: string;
    created: number;
    object: ChatCompletionType;
    choices: ChatResponseChoice[];
    history_metadata: {
        conversation_id: string;
        title: string;
        date: string;
    }
    error?: any;
}

export type ConversationRequest = {
    messages: ChatMessage[];
};

export type UserInfo = {
    access_token: string;
    expires_on: string;
    id_token: string;
    provider_name: string;
    user_claims: any[];
    user_id: string;
};

export enum CosmosDBStatus {
    NotConfigured = "CosmosDB is not configured",
    NotWorking = "CosmosDB is not working",
    Working = "CosmosDB is configured and working",
}

export type CosmosDBHealth = {
    cosmosDB: boolean,
    status: string
}

export enum ChatHistoryLoadingState {
    Loading = "loading",
    Success = "success",
    Fail = "fail",
    NotStarted = "notStarted"
}

export type ErrorMessage = {
    title: string,
    subtitle: string
}

// ── admin user-management types (mirror bloom-admin::user_mgmt) ──

export type RoleName = 'client' | 'behandelaar' | 'beheerder' | 'superbeheerder'

export type UserSummary = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: RoleName;
  /** Convenience boolean derived BE-side from suspended_at IS NOT NULL. */
  suspended: boolean;
  last_login_at: string | null;
  created_at: string;
  phone: string | null;
  /**
   * When this organisation ended its care relationship with the user
   * (CHL-068-009). Only present on the `membership=ended` listing.
   */
  care_ended_at?: string | null;
}

export type ListUsersResponse = {
  items: UserSummary[];
}

export type ListUsersParams = {
  q?: string;
  role?: RoleName;
  /** `true` = suspended only, `false` = active only, omit for both. */
  suspended?: boolean;
  /** BE clamps to [1, 200], default 50. */
  limit?: number;
  /** Rows to skip for offset pagination (default 0). */
  offset?: number;
  /**
   * `'ended'` lists the organisation's ended care relationships
   * (CHL-068-009); omit for the active ones.
   */
  membership?: 'active' | 'ended';
}

// ── behandelaar (supervisor) client-oversight types ──
// Mirror bloom-admin::clients::ClientView and ::user_mgmt::UserDetail.

export interface ClientView {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  /** When this client was assigned to the calling behandelaar. */
  assigned_at: string | null
  last_login_at: string | null
}

export interface ListMyClientsResponse {
  items: ClientView[]
}

export interface LoginActivityRow {
  success: boolean
  ip: string | null
  user_agent: string | null
  created_at: string
}

/** `GET /v1/supervisor/clients/{id}` — UserSummary (flattened) + counts. */
export interface ClientDetail extends UserSummary {
  period_count: number
  message_count: number
  login_activities_recent: LoginActivityRow[]
}

// ── behandelaar client read-only kalender + wellness (Module 9 Slice 2) ──

export type MenstruationIntensity = 'spotting' | 'light' | 'normal' | 'heavy'

export interface MenstruationDay {
  date: string
  intensity: MenstruationIntensity
}

/** `GET /v1/supervisor/clients/{id}/menstruation?month&year`. */
export interface MenstruationMonthView {
  days: MenstruationDay[]
}

export interface ClientChartSeries {
  name: string
  data: (number | null)[]
}

/** `GET /v1/supervisor/clients/{id}/feelings/chart?month&year` — mirrors
 * the client-facing wellness chart bundle. */
export interface ClientChartBundle {
  title: string
  categories: string[]
  dates: string[]
  chartData: ClientChartSeries[]
  phasesPerDay?: Record<string, string>
}

// ── knowledge base / RAG content upload (Tiket B) ──

/** `GET /v1/admin/knowledge` — RAG enablement + total chunk count. */
export interface KnowledgeSummary {
  enabled: boolean
  chunk_count: number
}

/** `POST /v1/admin/knowledge` request — one document = one POST. The
 * server chunks + embeds `content` (raw text/markdown). */
export interface IngestKnowledgeRequest {
  title?: string
  source_url?: string
  content: string
}

export interface IngestKnowledgeResponse {
  chunks_planned: number
  chunks_upserted: number
  chunks_failed: number
  title: string
}

/** A logical document grouped over `knowledge_chunks`
 * (`GET /v1/admin/knowledge/sources`). */
export interface KnowledgeSource {
  title: string
  source_url: string | null
  chunk_count: number
  created_at: string
}

export interface ListKnowledgeSourcesResponse {
  items: KnowledgeSource[]
}

export interface DeleteKnowledgeSourceResponse {
  chunks_deleted: number
}

/** A chat topic (`GET /v1/chat/categories`, 23-9-2026). Documents are
 * tagged with these so the chat searches the right ones. */
export interface ChatCategory {
  slug: string
  title: string
  description: string
  icon: string
  starter_questions: string[]
}

export type KnowledgeSourceType = 'article' | 'book' | 'course' | 'other'
export type KnowledgeDocumentStatus = 'draft' | 'approved'

/** A book, course or article as one chat source
 * (`/v1/admin/knowledge/documents`). Only `approved` ones are searched;
 * a book or course needs `rights_confirmed` before it can be approved. */
export interface KnowledgeDocument {
  id: string
  title: string
  source_type: KnowledgeSourceType
  author: string | null
  source_url: string | null
  categories: string[]
  rights_confirmed: boolean
  rights_note: string | null
  paraphrase_only: boolean
  status: KnowledgeDocumentStatus
  chunk_count: number
  created_at: string
  updated_at: string
}

export interface CreateKnowledgeDocumentRequest {
  title: string
  source_type?: KnowledgeSourceType
  author?: string
  source_url?: string
  categories?: string[]
  rights_confirmed?: boolean
  rights_note?: string
  paraphrase_only?: boolean
}

export type UpdateKnowledgeDocumentRequest = Partial<
  CreateKnowledgeDocumentRequest & { status: KnowledgeDocumentStatus }
>

export interface AddKnowledgeContentResponse {
  chunks_added: number
  chunk_count: number
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked'

export interface InvitedBySummary {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

export interface InvitationView {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  phone: string | null
  address: string | null
  invited_by: InvitedBySummary | null
  /** Role the account gets on acceptance (CHL-061-009). */
  role: RoleName
  status: InvitationStatus
  token: string
  invite_url: string
  expires_at: string
  consumed_at: string | null
  revoked_at: string | null
  created_at: string
}

export interface ListInvitationsParams {
  q?: string
  status?: InvitationStatus
  limit?: number
  offset?: number
}

export interface ListInvitationsResponse {
  items: InvitationView[]
}

export interface RevokeInvitationResult {
  id: string
  revoked_at: string
}

// ── Organisaties (multi-tenant, Tiket E4) ──────────────────────────────────
// `GET /v1/admin/organizations` returns a bare array of these.
export interface Organisatie {
  id: string
  slug: string
  name: string
  created_at: string
  user_count: number
  suspended: boolean
}

// `POST /v1/admin/organizations` body: org + its first beheerder.
export interface CreateOrgRequest {
  slug: string
  name: string
  admin: {
    email: string
    // Optional since CHL-053-009: login is by e-mail only.
    username?: string
    first_name: string
    last_name: string
    phone?: string
    password: string
  }
}

export interface CreateOrgResponse {
  organization_id: string
  slug: string
  name: string
  admin_id: string
  admin_email: string
  admin_username: string | null
}
// ── Platform feature flags (CHL-065-009) ───────────────────────────────────
// `GET /v1/admin/features`. `organizations: false` is the Bloom-only state:
// no organisation step anywhere, and no organisation data touched.
export interface PlatformFeatures {
  organizations: boolean
}
